/**
 * ============================================================================
 * ReAuthCeremony — B-5b (drafts/63 — ad-hoc step-up re-authentication).
 *
 * A reusable 21 CFR Part 11 step-up ceremony that ANY sensitive action can
 * invoke on demand (not tied to one approval flow — that's B-5). It:
 *   1. starts a signature challenge (POST /api/v1/signatures/challenge) to get
 *      the official attestation text + required factors,
 *   2. shows the attestation and collects the factor(s) — password, and TOTP
 *      when the challenge requires it,
 *   3. returns a signature reference (the challenge token) to the caller on
 *      confirm, or aborts on cancel.
 *
 * Usage: render <ReAuthCeremony open … onConfirm={(sig)=>…} onCancel={…} /> and
 * gate the sensitive action on onConfirm.
 * ============================================================================ */
import { useEffect, useState } from 'react';
import { Button } from '@edms/design-system';
import dialogStyles from '../../screens/AuthorWorkbench/NewDocumentForm.module.css';
import { apiFetch } from '../../api/client';

export interface ReAuthResult { signatureRef: string; meaning: string }
interface ChallengeResp {
  challenge_token: string;
  required_factors: string[];
  attestation_text: string;
  signature_meaning?: string;
}
interface ReAuthCeremonyProps {
  open: boolean;
  /** What is being signed (audit context). */
  targetType: string;
  targetId: string;
  /** The meaning of the signature, e.g. "Approved numbering-scheme change". */
  meaning: string;
  title?: string;
  onConfirm: (result: ReAuthResult) => void;
  onCancel: () => void;
}

export function ReAuthCeremony({ open, targetType, targetId, meaning, title, onConfirm, onCancel }: ReAuthCeremonyProps) {
  const [challenge, setChallenge] = useState<ChallengeResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Start a fresh challenge each time the ceremony opens.
  useEffect(() => {
    if (!open) { setChallenge(null); setPassword(''); setTotp(''); setError(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    apiFetch<ChallengeResp>('/api/v1/signatures/challenge', {
      method: 'POST',
      body: { target_type: targetType, target_id: targetId, signature_meaning: meaning },
    })
      .then((c) => { if (!cancelled) setChallenge(c); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not start the signature challenge.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, targetType, targetId, meaning]);

  if (!open) return null;
  const needsTotp = (challenge?.required_factors ?? []).includes('TOTP');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!password.trim()) { setError('Re-enter your password to authorise this action.'); return; }
    if (needsTotp && !totp.trim()) { setError('Enter your authenticator (TOTP) code.'); return; }
    if (!challenge) { setError('The challenge has not loaded yet.'); return; }
    setSubmitting(true);
    // The challenge token is the signature reference the caller hands to its
    // action endpoint. (A real Keycloak step-up verifies the factors at this
    // boundary; the mock issues the challenge as the auth surface.)
    try {
      onConfirm({ signatureRef: challenge.challenge_token, meaning });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={dialogStyles.backdrop} role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={dialogStyles.dialog} role="dialog" aria-modal="true" aria-labelledby="reauth-title">
        <header className={dialogStyles.header}>
          <h2 id="reauth-title" className={dialogStyles.title}>{title ?? 'Re-authenticate to continue'}</h2>
          <p className={dialogStyles.desc}>
            This is a controlled action. Confirm your identity to apply it — your electronic signature is recorded.
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={submit} noValidate>
          {error && <div role="alert" className={dialogStyles.errorBanner}>{error}</div>}
          {loading && <div className={dialogStyles.desc}>Starting signature challenge…</div>}

          {challenge && (
            <>
              <div className={dialogStyles.desc} role="note" style={{ borderLeft: '3px solid var(--color-brand-500, #2684ff)', paddingLeft: 10 }}>
                <strong>Meaning:</strong> {meaning}
                <br />
                <span style={{ fontSize: 12 }}>{challenge.attestation_text}</span>
              </div>

              <div className={dialogStyles.field}>
                <label htmlFor="reauth-pw" className={dialogStyles.label}>
                  Password<span className={dialogStyles.required} aria-hidden="true">*</span>
                </label>
                <input id="reauth-pw" type="password" autoComplete="current-password" className={dialogStyles.input}
                  value={password} onChange={(e) => setPassword(e.target.value)} autoFocus required />
              </div>

              {needsTotp && (
                <div className={dialogStyles.field}>
                  <label htmlFor="reauth-totp" className={dialogStyles.label}>
                    Authenticator code (TOTP)<span className={dialogStyles.required} aria-hidden="true">*</span>
                  </label>
                  <input id="reauth-totp" inputMode="numeric" pattern="[0-9]*" className={dialogStyles.input}
                    value={totp} onChange={(e) => setTotp(e.target.value)} placeholder="123456" />
                </div>
              )}
            </>
          )}

          <footer className={dialogStyles.footer}>
            <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={submitting} disabled={loading || !challenge}>
              Sign &amp; continue
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
